import React, { ChangeEvent, useEffect, useRef } from "react";

import _ from 'lodash';
import cx from 'classnames';

import { TabBar } from "../../../../../components/tabbar";
import { SectionBarProps, SectionState } from "../../../types";

import styles from './sections.module.scss';
import { AutoGrowInput } from "../../../../../components/input";
import { useDndMonitor } from "@dnd-kit/core";
import { rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { useNavigate } from "react-router-dom";
import Reveal from "../../../../../components/animation";

import { CollectionEditState } from "../../../../../types/collections";
import { AddSectionDialog } from "../../../components/content";

import { nanoid } from "nanoid";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";


const SectionBar: React.FC<SectionBarProps> = ({
  collection, sections, selected, menuState, onMenuChange, onSelectionChange, onRename, onRemove, onMove
}) => {
  const editing = menuState.primaryAction === 'edit';
  const tabRef = useRef<any>(null);
  const idRef = useRef(nanoid(6));

  const { trackAddSectionClicked } = useCollectionsAdoption();

  useEffect(() => {
    tabRef.current?.refresh();
  }, [editing, sections, menuState]);

  const handleAddSection = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'add-section'
    });

    trackAddSectionClicked(collection);
  }

  const handleRemoveSection = (section: SectionState) => {
    if (onRemove) {
      onRemove(section.index);
    }

    tabRef.current?.refresh();
  }

  const handleRename = (section: SectionState, value: string) => {
    if (onRename) {
      onRename(section.index, value);
    }

    tabRef.current?.refresh();
  }

  useDndMonitor({
    onDragOver(event) {
      const { active, over } = event;
      const sourceContainer = _.get(active, 'data.current.sortable.containerId');
      const section = _.get(active, 'data.current.section');
      const target = _.get(over, 'data.current.section');

      if (idRef.current === sourceContainer && section) {
        if (target && section.index !== target.index) {
          onMove(section.index, target.index);
        }

        tabRef.current?.refresh();
      }
    }
  });

  return (
    <SortableContext id={idRef.current} items={sections.map(section => section.index || 'new-section')} strategy={rectSortingStrategy}>
      <div className={cx(styles.content, { [styles.editing]: editing })}>
        <Reveal className={styles.addSectionButtonContainer} show={editing}>
          <AddSectionButton collection={collection} onAdd={handleAddSection} />
        </Reveal>
        <TabBar
          ref={tabRef}
          className={cx(styles.sectionsList, 'collection_sections')}
          gap={20}
          active={selected}
          onSelectionChange={onSelectionChange}
          showInitialAnimation={true}
        >
          {sections.map((section, index) => {
            return (
              <SectionItem section={section}
                editing={editing}
                key={section.uid}
                selected={selected === index}
                onNameChange={handleRename}
                onRemove={handleRemoveSection}
              />
            )
          })}
        </TabBar>
      </div>
    </SortableContext>
  );
}

const AddSectionButton: React.FC<{ collection: CollectionEditState, onAdd: () => void }> = ({ onAdd }) => {
  const handleClick = () => {
    onAdd();
  }

  return (
    <div className={styles.addSectionButton} onClick={handleClick}>
      <i className={cx(styles.icon, "paperflite-add")}></i>
      <div className={cx(styles.name, styles.add)}>Add Section</div>
      <div className={styles.seperator}></div>
    </div>
  );
}


interface SectionItemProps {
  section: SectionState;
  editing: boolean;
  selected: boolean;
  // eslint-disable-next-line no-unused-vars
  onRemove: (section: SectionState) => void;
  // eslint-disable-next-line no-unused-vars
  onNameChange: (section: SectionState, value: string) => void;
}

const SectionItem: React.FC<SectionItemProps> = ({ section, selected, editing, onNameChange, onRemove }) => {
  const [originalName, setOriginalName] = React.useState(section.name);

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({
    id: section.uid || nanoid(6),
    disabled: !editing,
    data: {
      section: section,
      type: 'section'
    }
  });

  useEffect(() => {
    setOriginalName(section.name);
  }, [editing]);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    onNameChange(section, e.target.value);
  }

  const handleRemove = () => {
    onRemove(section);
  }

  return (
    <div className={cx(styles.section, 'collection_section', {
      [styles.selected]: selected,
      [styles.editing]: editing,
      [styles.dragging]: isDragging
    })}
      ref={setNodeRef} {...listeners} {...attributes}
      data-section-index={section.index}
    >
      <AutoGrowInput
        readOnly={!(editing && selected)}
        className={styles.input}
        placeholder={originalName}
        value={section.name}
        onChange={handleNameChange}
      />

      {editing && selected &&
        <button className={styles.remove} onClick={handleRemove}>
          <i className="paperflite-close"></i>
        </button>
      }
    </div>
  );
}

const DraggingSectionCard: React.FC<{ section: SectionState }> = ({ section }) => {
  return (
    <div className={cx(styles.section, styles.dragPreview, 'collection_section')}>
      {section.name}
    </div>
  );
}

export { SectionBar, DraggingSectionCard };